/**
 * Geocoding service for subscriber address lookup.
 *
 * STRATEGY: Hierarchical elimination (City > Barangay > Street > Number)
 * 1. Geocode the STREET within the city/barangay to get a street anchor
 * 2. Geocode the full address (with house/bldg number) on that street
 * 3. Validate the result is actually on the correct street (not drifted)
 * 4. If exact number not found, pin stays on the correct street
 *
 * Uses Google Maps JavaScript Geocoder directly in the browser.
 */

// Frontend cache: address key -> result
const _cache = new Map();
const MAX_CACHE_SIZE = 200;

function cacheKey(address, barangay, municipality) {
  return `${(address || "").trim().toUpperCase()}|${(barangay || "").trim().toUpperCase()}|${(municipality || "").trim().toUpperCase()}`;
}

let _geocoder = null;

function getGeocoder() {
  if (_geocoder) return _geocoder;
  if (window.google && window.google.maps && window.google.maps.Geocoder) {
    _geocoder = new window.google.maps.Geocoder();
  }
  return _geocoder;
}

// ---------------------------------------------------------------------------
// Address parsing utilities
// ---------------------------------------------------------------------------

/**
 * Parse a Philippine address into structured components.
 * Examples:
 *   "10-B KANLAON STREET BRGY HIGHWAY HILLS" -> { number: "10-B", street: "KANLAON STREET" }
 *   "BLK 4 LOT 15 ABRAHAM ST STA LUCIA BLISS" -> { number: "BLK 4 LOT 15", street: "ABRAHAM ST" }
 *   "UNIT 5 GREENFIELD RESIDENCES SHAW BLVD" -> { number: "UNIT 5", building: "GREENFIELD RESIDENCES", street: "SHAW BLVD" }
 */
function parseAddress(rawAddress, barangay) {
  let addr = (rawAddress || "").trim().toUpperCase();
  const brgy = (barangay || "").trim().toUpperCase();

  // Remove embedded barangay references
  if (brgy) {
    addr = addr.replace(new RegExp(`\\b(?:BRGY\\.?|BARANGAY)\\s+${escapeRegex(brgy)}\\b`, "i"), "").trim();
    addr = addr.replace(new RegExp(`\\b(?:BRGY\\.?|BARANGAY)\\s+[A-Z0-9][A-Z0-9\\s]*$`, "i"), "").trim();
    // Remove trailing standalone barangay name
    const trailingBrgy = new RegExp(`[,\\s]+${escapeRegex(brgy)}\\s*$`, "i");
    addr = addr.replace(trailingBrgy, "").trim();
    // Also try without comma
    if (addr.endsWith(brgy)) {
      addr = addr.slice(0, addr.length - brgy.length).trim();
    }
  }

  // Remove city/municipality names often appended at end
  addr = addr.replace(/[,\s]+(?:CITY OF\s+)?(?:MANDALUYONG|PASIG|MARIKINA|CAINTA|SAN JUAN|QUEZON CITY|MAKATI|TAGUIG|PATEROS)(?:\s+CITY)?\s*$/i, "").trim();

  // Clean trailing commas/dashes
  addr = addr.replace(/[,\-\s]+$/, "").trim();

  // --- Extract components ---
  let number = "";
  let street = "";
  let building = "";
  let subdivision = "";

  // Extract subdivision/village (often at the end)
  const subdivMatch = addr.match(/\b([\w\s]*?(?:SUBD(?:IVISION)?|VILLAGE|VILL|RESIDENCES?|TOWNHOUSE|HOMES|ESTATE|BLISS|COURT|GARDENS?|ENCLAVE|COMPOUND)(?:\s+(?:PHASE|PH)\s*\d+)?)\s*$/i);
  if (subdivMatch && subdivMatch.index > 0) {
    subdivision = subdivMatch[1].trim();
    addr = addr.slice(0, subdivMatch.index).trim();
  }

  // Extract block/lot/unit prefix as "number"
  const blkLotMatch = addr.match(/^((?:(?:BLK|BLOCK|LOT|UNIT|FLR|FLOOR|RM|ROOM)\s*\.?\s*\d+[-/A-Z]*[,\s]*)+)/i);
  if (blkLotMatch) {
    number = blkLotMatch[1].replace(/[,\s]+$/, "").trim();
    addr = addr.slice(blkLotMatch[0].length).trim();
  }

  // Extract leading house number (e.g., "10-B", "#123", "45")
  if (!number) {
    const houseMatch = addr.match(/^([#]?\d+[-/]?[A-Z]?)\s+/i);
    if (houseMatch) {
      number = houseMatch[1].trim();
      addr = addr.slice(houseMatch[0].length).trim();
    }
  }

  // Street suffix patterns
  const STREET_SUFFIX_RE = /\b(?:STREET|ST|AVENUE|AVE|BOULEVARD|BLVD|ROAD|RD|DRIVE|DR|LANE|LN|CIRCLE|CIR|WAY|CRESCENT|CRES|HIGHWAY|HWY|ESPLANADE)\b\.?/i;

  // Try to find the street portion (text containing a street suffix)
  const streetMatch = addr.match(new RegExp(`^(.+?${STREET_SUFFIX_RE.source})(.*)$`, "i"));
  if (streetMatch) {
    street = streetMatch[1].trim();
    const remainder = (streetMatch[2] || "").trim();
    if (remainder) {
      if (/SUBD|VILLAGE|VILL|RESIDENCES?|BLISS|HOMES|ESTATE|COMPOUND/i.test(remainder)) {
        if (!subdivision) subdivision = remainder;
      } else if (/BLDG|BUILDING|TOWER|CONDO|PLAZA|MALL|CENTER/i.test(remainder)) {
        building = remainder;
      }
    }
  } else {
    // No street suffix found — use entire remaining text as street
    street = addr;
  }

  // If building is embedded in front of street ("GREENFIELD RESIDENCES SHAW BLVD")
  if (!building && street) {
    const bldgInStreet = street.match(/^(.+?(?:RESIDENCES?|TOWER|BUILDING|BLDG|CONDO(?:MINIUM)?|PLAZA|MALL|CENTER|CENTRE))\s+(.+)$/i);
    if (bldgInStreet) {
      building = bldgInStreet[1].trim();
      street = bldgInStreet[2].trim();
    }
  }

  // Remove commas from extracted street
  street = street.replace(/^[,\s]+|[,\s]+$/g, "").trim();

  // CRITICAL: If no street was found but we have a subdivision/compound name,
  // that IS the location name (e.g. "MOLAVE COMPOUND" is the place, not a suffix).
  // In Philippine addressing, compound/village names ARE the primary location when
  // there's no separate street name.
  if (!street && subdivision) {
    street = subdivision;
    subdivision = "";
  }

  return {
    number: number || "",
    street: street || "",
    building: building || "",
    subdivision: subdivision || "",
    cleaned: [number, street].filter(Boolean).join(" "),
  };
}

/**
 * Normalize street name for comparison — strips suffixes and punctuation.
 * "KANLAON ST." -> "KANLAON"
 * "J. RIZAL AVENUE" -> "J RIZAL"
 */
function normalizeStreetForCompare(street) {
  let s = (street || "").trim().toUpperCase();
  // Remove street suffixes
  s = s.replace(/\b(?:STREET|ST\.?|AVENUE|AVE\.?|BOULEVARD|BLVD\.?|ROAD|RD\.?|DRIVE|DR\.?|LANE|LN\.?|CIRCLE|CIR\.?|WAY|CRESCENT|CRES\.?|COURT|CT\.?|PLACE|PL\.?|HIGHWAY|HWY\.?|ESPLANADE)\b/gi, "").trim();
  // Remove periods from initials
  s = s.replace(/\./g, "");
  // Collapse spaces
  s = s.replace(/\s+/g, " ").trim();
  return s;
}

/**
 * Check if two street names likely refer to the same street.
 * Handles abbreviations, old/new names, and partial matches.
 */
function streetsMatch(street1, street2) {
  const n1 = normalizeStreetForCompare(street1);
  const n2 = normalizeStreetForCompare(street2);
  if (!n1 || !n2) return false;
  if (n1 === n2) return true;
  // One contains the other
  if (n1.includes(n2) || n2.includes(n1)) return true;
  // Check significant word overlap (for multi-word streets)
  const words1 = n1.split(" ").filter((w) => w.length > 1);
  const words2 = n2.split(" ").filter((w) => w.length > 1);
  if (words1.length === 0 || words2.length === 0) return false;
  const common = words1.filter((w) => words2.includes(w));
  // At least one significant shared word, and it covers the majority of the shorter name
  const minLen = Math.min(words1.length, words2.length);
  return common.length >= 1 && common.length >= minLen * 0.6;
}

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Compute approximate distance in meters between two lat/lng points (Haversine).
 */
function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Known municipality centers for city-level validation (Metro Manila)
const MUNICIPALITY_CENTERS = {
  "MANDALUYONG": [14.5794, 121.0359],
  "CITY OF MANDALUYONG": [14.5794, 121.0359],
  "PASIG": [14.5764, 121.0851],
  "CITY OF PASIG": [14.5764, 121.0851],
  "MARIKINA": [14.6507, 121.1029],
  "CITY OF MARIKINA": [14.6507, 121.1029],
  "CAINTA": [14.5780, 121.1192],
  "SAN JUAN": [14.6019, 121.0355],
  "CITY OF SAN JUAN": [14.6019, 121.0355],
  "QUEZON CITY": [14.6760, 121.0437],
  "MAKATI": [14.5547, 121.0244],
  "CITY OF MAKATI": [14.5547, 121.0244],
  "TAGUIG": [14.5176, 121.0509],
  "CITY OF TAGUIG": [14.5176, 121.0509],
  "PATEROS": [14.5446, 121.0686],
  "ANTIPOLO": [14.5860, 121.1761],
  "CITY OF ANTIPOLO": [14.5860, 121.1761],
  "TAYTAY": [14.5575, 121.1326],
  "RIZAL": [14.5575, 121.1326],
};

// Max acceptable distance from municipality center (meters)
const MAX_CITY_DISTANCE_M = 8000;
// Max distance a "number" result can drift from the street anchor (meters)
// Typical Metro Manila barangay street: 200m-800m
const MAX_STREET_DRIFT_M = 600;

// ---------------------------------------------------------------------------
// Google Geocoder wrapper
// ---------------------------------------------------------------------------

function geocodeWithGoogle(query, attempt = 0) {
  return new Promise((resolve) => {
    const geocoder = getGeocoder();
    if (!geocoder) {
      resolve(null);
      return;
    }
    const timeout = setTimeout(() => {
      console.warn("[Geocoding] Timeout:", query);
      resolve(null);
    }, 8000);
    geocoder.geocode(
      { address: query, region: "ph", componentRestrictions: { country: "PH" } },
      (results, status) => {
        clearTimeout(timeout);
        if (status === "OK" && results && results.length > 0) {
          resolve(results);
        } else if (status === "OVER_QUERY_LIMIT" && attempt < 4) {
          // Rate-limited (matters when geocoding in bulk). Back off and retry
          // with exponential delay rather than dropping the result.
          const delay = 500 * Math.pow(2, attempt);
          setTimeout(() => resolve(geocodeWithGoogle(query, attempt + 1)), delay);
        } else {
          resolve(null);
        }
      }
    );
  });
}

/**
 * Extract the street/route name from a Google geocode result's address_components.
 */
function getRouteFromResult(result) {
  if (!result || !result.address_components) return "";
  for (const comp of result.address_components) {
    if (comp.types.includes("route")) {
      return (comp.long_name || comp.short_name || "").toUpperCase();
    }
  }
  return "";
}

/**
 * Extract the locality/city from a Google geocode result's address_components.
 */
function getCityFromResult(result) {
  if (!result || !result.address_components) return "";
  for (const comp of result.address_components) {
    if (comp.types.includes("locality") || comp.types.includes("administrative_area_level_2")) {
      return (comp.long_name || "").toUpperCase();
    }
  }
  return "";
}

/**
 * Extract the sublocality (barangay) from a Google geocode result.
 */
function getBarangayFromResult(result) {
  if (!result || !result.address_components) return "";
  for (const comp of result.address_components) {
    if (comp.types.includes("sublocality_level_1") || comp.types.includes("sublocality") || comp.types.includes("neighborhood")) {
      return (comp.long_name || "").toUpperCase();
    }
  }
  return "";
}

function determineAccuracy(result) {
  const locationType = result.geometry?.location_type;
  const types = result.types || [];

  if (locationType === "ROOFTOP") return "exact";
  if (locationType === "RANGE_INTERPOLATED") return "address";
  if (locationType === "GEOMETRIC_CENTER") {
    if (types.includes("route")) return "street";
    if (types.includes("sublocality") || types.includes("sublocality_level_1")) return "barangay";
    return "street";
  }
  if (locationType === "APPROXIMATE") {
    if (types.includes("sublocality") || types.includes("sublocality_level_1") || types.includes("neighborhood")) return "barangay";
    if (types.includes("locality") || types.includes("administrative_area_level_2")) return "municipality";
    return "barangay";
  }
  return "address";
}

/**
 * Check if a result is within the expected city.
 */
function isInCity(lat, lng, municipality) {
  const muni = (municipality || "").trim().toUpperCase();
  const muniShort = muni.replace(/^CITY OF\s+/i, "");
  const muniCenter = MUNICIPALITY_CENTERS[muni] || MUNICIPALITY_CENTERS[`CITY OF ${muniShort}`] || MUNICIPALITY_CENTERS[muniShort];
  if (!muniCenter) return true; // can't validate, allow
  return haversineMeters(lat, lng, muniCenter[0], muniCenter[1]) <= MAX_CITY_DISTANCE_M;
}

// ---------------------------------------------------------------------------
// Hierarchical geocoding: City > Barangay > Street > Number
// ---------------------------------------------------------------------------

/**
 * STEP 1: Find the street anchor — geocode the street name within the city/barangay.
 * This ensures we have a validated reference point ON the correct street/place.
 * For compound/village names, we accept place-type results (not just routes).
 */
async function findStreetAnchor(parsed, barangay, municipality) {
  const street = parsed.street;
  if (!street) return null;

  const muni = (municipality || "").trim().toUpperCase();
  const brgy = (barangay || "").trim().toUpperCase();
  const muniShort = muni.replace(/^CITY OF\s+/i, "");

  // Detect if the "street" is actually a compound/village/place name (not a road)
  const isPlaceName = /\b(?:COMPOUND|COMPD|VILLAGE|VILL|SUBD|SUBDIVISION|RESIDENCES?|TOWNHOUSE|HOMES|ESTATE|ENCLAVE|COURT|GARDENS?)\b/i.test(street);

  // Queries specifically for the STREET/PLACE (no house number)
  const streetQueries = [];
  if (brgy) {
    streetQueries.push(`${street}, Barangay ${brgy}, ${muniShort}, Metro Manila, Philippines`);
    streetQueries.push(`${street}, ${brgy}, ${muniShort}, Philippines`);
  }
  streetQueries.push(`${street}, ${muniShort}, Metro Manila, Philippines`);
  streetQueries.push(`${street}, ${muniShort}, Philippines`);

  // For actual street names (not compounds), also try with "Street" suffix
  if (!isPlaceName) {
    const hasStreetSuffix = /\b(?:ST|STREET|AVE|AVENUE|BLVD|BOULEVARD|RD|ROAD|DR|DRIVE|LN|LANE|HWY|HIGHWAY)\b\.?$/i.test(street);
    if (!hasStreetSuffix) {
      if (brgy) {
        streetQueries.push(`${street} Street, Barangay ${brgy}, ${muniShort}, Philippines`);
      }
      streetQueries.push(`${street} Street, ${muniShort}, Philippines`);
    }
  }

  for (const query of streetQueries) {
    const results = await geocodeWithGoogle(query);
    if (!results) continue;

    for (const result of results) {
      const lat = result.geometry.location.lat();
      const lng = result.geometry.location.lng();
      const resultRoute = getRouteFromResult(result);
      const types = result.types || [];
      const formAddr = (result.formatted_address || "").toUpperCase();

      // Must be within the correct city
      if (!isInCity(lat, lng, municipality)) continue;

      // For place names (compound/village): accept neighborhood, premise,
      // establishment, or sublocality results — they are the valid anchor
      if (isPlaceName) {
        const isPlaceType = types.some((t) =>
          ["neighborhood", "sublocality", "sublocality_level_1", "premise",
           "establishment", "point_of_interest", "route", "geocode"].includes(t)
        );
        // Also check if formatted address or route contains our place name
        const streetNorm = normalizeStreetForCompare(street);
        const nameInResult = (streetNorm && streetNorm.length >= 3) &&
          (formAddr.includes(streetNorm) || (resultRoute && streetsMatch(street, resultRoute)));

        if (isPlaceType || nameInResult) {
          // Extra validation: if barangay is specified, check it matches
          if (brgy) {
            const resultBrgy = getBarangayFromResult(result);
            // Only reject if we can verify it's a different barangay
            if (resultBrgy && resultBrgy !== brgy && !resultBrgy.includes(brgy) && !brgy.includes(resultBrgy)) {
              console.log("[Geocoding] ✗ Rejected anchor (wrong barangay:", resultBrgy, "expected:", brgy, ")");
              continue;
            }
          }
          console.log("[Geocoding] ✓ Place anchor:", query, "→", result.formatted_address, `(${lat.toFixed(6)}, ${lng.toFixed(6)})`);
          return { lat, lng, route: street, result, query };
        }
        continue;
      }

      // For regular street names: prefer route-type results
      const isRouteType = types.includes("route");
      const routeMatches = resultRoute && streetsMatch(street, resultRoute);

      if (routeMatches || isRouteType) {
        console.log("[Geocoding] ✓ Street anchor:", query, "→", result.formatted_address, `(${lat.toFixed(6)}, ${lng.toFixed(6)})`);
        return { lat, lng, route: resultRoute || street, result, query };
      }

      // Accept if formatted_address contains normalized street name
      const streetNorm = normalizeStreetForCompare(street);
      if (streetNorm && streetNorm.length >= 3 && formAddr.includes(streetNorm)) {
        console.log("[Geocoding] ✓ Street anchor (name in address):", query, "→", result.formatted_address);
        return { lat, lng, route: resultRoute || street, result, query };
      }
    }
  }

  console.log("[Geocoding] ✗ No street/place anchor found for:", street, "in", muniShort, brgy || "");
  return null;
}

/**
 * STEP 2: Find exact location (house/building number) constrained to the street/place.
 * The result MUST be within MAX_STREET_DRIFT_M of the street anchor.
 */
async function findExactOnStreet(parsed, barangay, municipality, streetAnchor) {
  const { number, street, building, subdivision } = parsed;
  const muni = (municipality || "").trim().toUpperCase();
  const brgy = (barangay || "").trim().toUpperCase();
  const muniShort = muni.replace(/^CITY OF\s+/i, "");

  if (!number && !building) return null;

  // Is this a compound/village/place name rather than a street?
  const isPlaceName = /\b(?:COMPOUND|COMPD|VILLAGE|VILL|SUBD|SUBDIVISION|RESIDENCES?|TOWNHOUSE|HOMES|ESTATE|ENCLAVE|COURT|GARDENS?)\b/i.test(street);

  const exactQueries = [];
  const fullAddr = [number, street].filter(Boolean).join(" ");

  // Most precise: full address with number
  if (number && street) {
    if (brgy) {
      exactQueries.push(`${fullAddr}, Barangay ${brgy}, ${muniShort}, Metro Manila, Philippines`);
      exactQueries.push(`${fullAddr}, ${brgy}, ${muniShort}, Philippines`);
    }
    exactQueries.push(`${fullAddr}, ${muniShort}, Metro Manila, Philippines`);
    exactQueries.push(`${fullAddr}, ${muniShort}, Philippines`);
  }

  // Building + street
  if (building && street) {
    exactQueries.push(`${building}, ${street}, ${muniShort}, Philippines`);
    if (number) {
      exactQueries.push(`${number} ${building}, ${street}, ${muniShort}, Philippines`);
    }
  }

  // With subdivision context
  if (subdivision && street && number) {
    exactQueries.push(`${fullAddr}, ${subdivision}, ${muniShort}, Philippines`);
  }

  for (const query of exactQueries) {
    const results = await geocodeWithGoogle(query);
    if (!results) continue;

    for (const result of results) {
      const lat = result.geometry.location.lat();
      const lng = result.geometry.location.lng();
      const resultRoute = getRouteFromResult(result);
      const accuracy = determineAccuracy(result);

      // GATE 1: Must be in correct city
      if (!isInCity(lat, lng, municipality)) continue;

      // GATE 2: Must not drift from street/place anchor
      const streetDist = haversineMeters(lat, lng, streetAnchor.lat, streetAnchor.lng);
      if (streetDist > MAX_STREET_DRIFT_M) {
        console.log("[Geocoding] ✗ Rejected exact (drifted", Math.round(streetDist), "m from anchor):", result.formatted_address);
        continue;
      }

      // GATE 3: For street names, verify the route matches.
      // For place names (compound/village), skip strict route check since
      // Google may not return a "route" component for compound addresses.
      if (!isPlaceName && resultRoute && parsed.street) {
        if (!streetsMatch(parsed.street, resultRoute)) {
          console.log("[Geocoding] ✗ Rejected exact (wrong street:", resultRoute, "expected:", parsed.street, ")");
          continue;
        }
      }

      console.log("[Geocoding] ✓ Exact location:", query, "→", result.formatted_address, accuracy, `(${lat.toFixed(6)}, ${lng.toFixed(6)})`, `dist=${Math.round(streetDist)}m`);
      return { lat, lng, accuracy, result, query };
    }
  }

  return null;
}

/**
 * STEP 3: Fallback — find the barangay center.
 */
async function findBarangayFallback(barangay, municipality) {
  const brgy = (barangay || "").trim().toUpperCase();
  const muni = (municipality || "").trim().toUpperCase();
  const muniShort = muni.replace(/^CITY OF\s+/i, "");

  if (!brgy || !muni) return null;

  const queries = [
    `Barangay ${brgy}, ${muniShort}, Metro Manila, Philippines`,
    `Barangay ${brgy}, ${muniShort}, Philippines`,
    `${brgy}, ${muniShort}, Philippines`,
  ];

  for (const query of queries) {
    const results = await geocodeWithGoogle(query);
    if (!results) continue;

    for (const result of results) {
      const lat = result.geometry.location.lat();
      const lng = result.geometry.location.lng();
      if (!isInCity(lat, lng, municipality)) continue;

      console.log("[Geocoding] ✓ Barangay fallback:", result.formatted_address);
      return { lat, lng, accuracy: "barangay", result };
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Geocode a subscriber address using hierarchical elimination strategy.
 *
 * Priority: City > Barangay > Street > Building/House Number
 * The pin will NEVER leave the correct street if a street match is found.
 *
 * @param {Object} params
 * @param {string} params.address - Full address
 * @param {string} params.barangay - Barangay name
 * @param {string} params.municipality - Municipality/city name
 * @param {number|null} params.latitude - Existing latitude if available
 * @param {number|null} params.longitude - Existing longitude if available
 * @returns {Promise<{success, latitude, longitude, accuracy, message}>}
 */
export async function geocodeSubscriberAddress({ address, barangay, municipality, latitude, longitude }) {
  // If existing coordinates are available, use them directly
  if (latitude && longitude && !isNaN(latitude) && !isNaN(longitude)) {
    return {
      success: true,
      latitude: parseFloat(latitude),
      longitude: parseFloat(longitude),
      accuracy: "exact",
      message: "Subscriber location found.",
    };
  }

  const key = cacheKey(address, barangay, municipality);
  if (_cache.has(key)) return _cache.get(key);

  // Wait for Google Maps to load (up to 5 seconds)
  let attempts = 0;
  while (!getGeocoder() && attempts < 25) {
    await new Promise((r) => setTimeout(r, 200));
    attempts++;
  }
  if (!getGeocoder()) {
    return { success: false, latitude: null, longitude: null, accuracy: null, message: "Google Maps not loaded. Please refresh the page." };
  }

  // Parse the address into components
  const parsed = parseAddress(address, barangay);
  console.log("[Geocoding] Parsed:", JSON.stringify(parsed), "| Brgy:", barangay, "| City:", municipality);

  let finalResult = null;

  // === STEP 1: Find the STREET anchor ===
  const streetAnchor = await findStreetAnchor(parsed, barangay, municipality);

  if (streetAnchor) {
    // === STEP 2: Find EXACT location on the street ===
    const exact = await findExactOnStreet(parsed, barangay, municipality, streetAnchor);

    if (exact) {
      finalResult = {
        latitude: exact.lat,
        longitude: exact.lng,
        accuracy: exact.accuracy,
        display_name: exact.result.formatted_address || "",
      };
    } else {
      // Exact number not resolved — pin on the street (still very useful)
      finalResult = {
        latitude: streetAnchor.lat,
        longitude: streetAnchor.lng,
        accuracy: "street",
        display_name: streetAnchor.result.formatted_address || "",
      };
    }
  } else {
    // Street not found — try full address with city validation only
    const muni = (municipality || "").trim().toUpperCase();
    const muniShort = muni.replace(/^CITY OF\s+/i, "");
    const brgy = (barangay || "").trim().toUpperCase();

    const fullQueries = [];
    if (parsed.cleaned && brgy) {
      fullQueries.push(`${parsed.cleaned}, Barangay ${brgy}, ${muniShort}, Metro Manila, Philippines`);
      fullQueries.push(`${parsed.cleaned}, ${brgy}, ${muniShort}, Philippines`);
    }
    if (parsed.cleaned) {
      fullQueries.push(`${parsed.cleaned}, ${muniShort}, Philippines`);
    }
    if (parsed.subdivision && brgy) {
      fullQueries.push(`${parsed.subdivision}, Barangay ${brgy}, ${muniShort}, Philippines`);
    }

    for (const query of fullQueries) {
      const results = await geocodeWithGoogle(query);
      if (!results) continue;
      for (const result of results) {
        const lat = result.geometry.location.lat();
        const lng = result.geometry.location.lng();
        if (!isInCity(lat, lng, municipality)) continue;
        const accuracy = determineAccuracy(result);
        console.log("[Geocoding] Full address fallback:", result.formatted_address, accuracy);
        finalResult = { latitude: lat, longitude: lng, accuracy, display_name: result.formatted_address || "" };
        break;
      }
      if (finalResult) break;
    }

    // Last resort: barangay centroid
    if (!finalResult) {
      const brgyFallback = await findBarangayFallback(barangay, municipality);
      if (brgyFallback) {
        finalResult = {
          latitude: brgyFallback.lat,
          longitude: brgyFallback.lng,
          accuracy: "barangay",
          display_name: brgyFallback.result.formatted_address || "",
        };
      }
    }
  }

  // Build response
  if (finalResult) {
    const messages = {
      exact: "Subscriber location found (exact match).",
      address: "Subscriber location found.",
      street: "Pinned to correct street. Exact number not resolved.",
      barangay: "Street not found. Showing barangay area.",
      municipality: "Showing municipality area.",
    };

    const geocodeResult = {
      success: true,
      latitude: finalResult.latitude,
      longitude: finalResult.longitude,
      accuracy: finalResult.accuracy,
      display_name: finalResult.display_name,
      message: messages[finalResult.accuracy] || "Location found.",
    };

    // Cache
    if (_cache.size >= MAX_CACHE_SIZE) {
      const firstKey = _cache.keys().next().value;
      _cache.delete(firstKey);
    }
    _cache.set(key, geocodeResult);
    return geocodeResult;
  }

  return { success: false, latitude: null, longitude: null, accuracy: null, message: "Unable to locate subscriber address." };
}

export default geocodeSubscriberAddress;
